import './index.css'
import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import AuthRoute from 'components/Route/AuthRoute'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { ReactChannelIO } from 'react-channel-plugin'
import ScrollToTop from 'components/common/ScrollToTop'
import PageLogger from 'components/common/PageLogger'
import InAppAlert from 'components/InAppAlert'
import SamsungAlert from 'components/SamsungAlert'
import Toast from 'components/common/Toast'
import { Popup } from 'components/Popup'
import LoadCommonData from 'components/common/LoadCommonData'
import { RENEWAL_AT_ROOT } from 'components/Renewal/paths'
import RenewalRoutes, { RenewalNotFound, renewalRouteElements } from 'pages/RenewalPage/RenewalRoutes'

const AuthPage = lazy(() => import('pages/AuthPage'))
const PaymentHistoryPage = lazy(() => import('pages/PaymentHistoryPage'))
const CouponPage = lazy(() => import('pages/CouponPage'))
const PointPage = lazy(() => import('pages/PointPage'))
const NoticePage = lazy(() => import('pages/NoticePage'))
const TempVerifyPage = lazy(() => import('pages/TempVerifyPage/TempVerifyPage'))
const RedirectPage = lazy(() => import('pages/RedirectPage'))
const ClosePage = lazy(() => import('pages/ClosePage'))
const MyPage = lazy(() => import('pages/MyPage'))
const StorePostPage = lazy(() => import('pages/StorePostPage'))
const ServicePaymentPage = lazy(() => import('pages/ServicePaymentPage'))
const ServiceCompletePage = lazy(() => import('pages/ServiceCompletePage'))
const StoreDetailPage = lazy(() => import('pages/StoreDetailPage'))
const StoreModifyPage = lazy(() => import('pages/StoreModifyPage'))
const FaqPage = lazy(() => import('pages/FaqPage'))
const StorePage = lazy(() => import('pages/StorePage'))
const MapPage = lazy(() => import('pages/MapPage'))
const ServiceDetailPage = lazy(() => import('pages/ServiceDetailPage'))
const ServicePostPage = lazy(() => import('pages/ServicePostPage'))
const EasyConsultingPage = lazy(() => import('pages/EasyConsultingPage'))
const ConsultingPage = lazy(() => import('pages/ConsultingPage'))
const CsPrePage = lazy(() => import('pages/CsPrePage'))
const NewCrewPage = lazy(() => import('pages/NewCrewPage'))
const NewCrewCompletePage = lazy(() => import('pages/NewCrewCompletePage'))
const WooribankPage = lazy(() => import('pages/WooriBankPage'))
const ShinhanCardPage = lazy(() => import('pages/ShinhanCardPage'))

Sentry.init({
  dsn: 'https://45805afa158e4b3bb77977a062d09277@o269300.ingest.sentry.io/5840548',
  integrations: [new Integrations.BrowserTracing()],
  environment: process.env.REACT_APP_ENV,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
})

export default function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: Infinity,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  })

  /**
   * @function androidMobileFullHeightSetting
   * @description 안드로이드기기에서 100vh를 계산해주는 함수
   */
  function androidMobileFullHeightSetting() {
    let vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--aosvh', `${vh}px`)
  }

  useEffect(() => {
    document.body.classList.add('brwnie')
    androidMobileFullHeightSetting()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Helmet>
          {process.env.REACT_APP_ENV === 'develop' ? <meta name="robots" content="noindex,nofollow" /> : <></>}
          <script src="https://developers.kakao.com/sdk/js/kakao.js"></script>
        </Helmet>
        <ReactChannelIO
          pluginKey="8f6d65f6-c568-4b94-801b-a3637a605826"
          autoBoot
          zIndex={10000005}
          // (신규 크루 정보 입력 & 입력 완료) + (브라우니 x 우리은행) 페이지에서는 채널톡 숨김
          hideChannelButtonOnBoot={
            window.location.href.includes('new-crew') || window.location.href.includes('wooribank')
          }
        >
          <Router>
            <LoadCommonData />
            <ScrollToTop />
            <PageLogger />
            <Suspense fallback={null}>
            <Switch>
              {RENEWAL_AT_ROOT ? renewalRouteElements() : null}
              {!RENEWAL_AT_ROOT && (
                <Route path="/renewal">
                  <RenewalRoutes />
                </Route>
              )}

              <Route path="/shinhancard" exact>
                <ShinhanCardPage />
              </Route>

              <Route path="/wooribank" exact>
                <WooribankPage />
              </Route>

              {!RENEWAL_AT_ROOT && (
                <Route path="/easy-consulting">
                  <EasyConsultingPage />
                </Route>
              )}
              <AuthRoute path="/service-post/:storeId">
                <ServicePostPage />
              </AuthRoute>
              <Route path="/auth">
                <AuthPage />
              </Route>
              <AuthRoute path="/payment">
                <PaymentHistoryPage />
              </AuthRoute>
              <AuthRoute path="/coupon">
                <CouponPage />
              </AuthRoute>
              <AuthRoute path="/notice">
                <NoticePage />
              </AuthRoute>
              <AuthRoute path="/mypage">
                <MyPage />
              </AuthRoute>
              <Route path="/verify">
                <TempVerifyPage />
              </Route>
              <AuthRoute path="/point">
                <PointPage />
              </AuthRoute>
              <Route path="/close">
                <ClosePage />
              </Route>
              <AuthRoute path="/service-payment/:serviceId">
                <ServicePaymentPage />
              </AuthRoute>
              <AuthRoute path="/service-complete/:serviceId">
                <ServiceCompletePage />
              </AuthRoute>
              <AuthRoute path="/store-post">
                <StorePostPage />
              </AuthRoute>
              <AuthRoute path="/store-detail/:storeId">
                <StoreDetailPage />
              </AuthRoute>
              {!RENEWAL_AT_ROOT && (
                <Route path="/faq">
                  <FaqPage />
                </Route>
              )}
              {!RENEWAL_AT_ROOT && (
                <Route path="/map">
                  <MapPage />
                </Route>
              )}
              <AuthRoute path="/store-modify/:storeId">
                <StoreModifyPage />
              </AuthRoute>
              {!RENEWAL_AT_ROOT && (
                <Route path="/service-detail/:type">
                  <ServiceDetailPage />
                </Route>
              )}
              <AuthRoute path="/store">
                <StorePage />
              </AuthRoute>
              {!RENEWAL_AT_ROOT && (
                <Route path="/muin-consulting">
                  <ConsultingPage />
                </Route>
              )}
              <Route path="/cs-pre">
                <CsPrePage />
              </Route>
              <Route path="/new-crew">
                <NewCrewPage />
              </Route>
              <Route path="/new-crew-complete">
                <NewCrewCompletePage />
              </Route>
              <Route path="/redirect">
                <RedirectPage />
              </Route>
              {RENEWAL_AT_ROOT ? (
                <Route path="*">
                  <RenewalNotFound />
                </Route>
              ) : (
                <Redirect to="/" path="*" />
              )}
            </Switch>
            </Suspense>
          </Router>
        </ReactChannelIO>
      </HelmetProvider>
      {/* 모달 */}
      <SamsungAlert />
      <InAppAlert />
      <Toast />
    </QueryClientProvider>
  )
}
